/**
 * Get effective price for a variation using price + variation_item_discount + variation_item_discount_type.
 * Matches the calculation used in AddQuantityModal so cart/checkout show the same price the user selected.
 * @param {Object} variation - Variation object with price, variation_item_discount, variation_item_discount_type
 * @returns {number} Effective (discounted) price per unit
 */
export const getVariationEffectivePrice = (variation) => {
  if (!variation) return 0;
  const originalPrice = parseFloat(variation.price || 0);
  const discount = parseFloat(variation.variation_item_discount || 0);
  const discountType = (variation.variation_item_discount_type || '').toLowerCase();

  if (!originalPrice) return 0;
  if (discount <= 0 || (discountType !== 'percentage' && discountType !== 'fixed')) {
    return originalPrice;
  }

  if (discountType === 'percentage') {
    const discounted = originalPrice - (originalPrice * discount / 100);
    return Math.max(0, discounted);
  }
  if (discountType === 'fixed') {
    return Math.max(0, originalPrice - discount);
  }
  return originalPrice;
};

/**
 * Get add-ons that belong to this cart item (present in item.add_ons or variation.add_ons).
 * Prevents showing or counting add-ons from another item (e.g. "onion" from Peasara under Egg Dosa).
 * @param {Object} item - Cart item with selectedAddOns, add_ons, variation.add_ons
 * @returns {Array} Filtered list of selected add-ons that belong to this menu item
 */
export const getValidAddonsForCartItem = (item) => {
  const raw = item.selectedAddOns || item.currentAddons || [];
  if (!raw.length) return [];
  let addonsList = item.add_ons;
  if (item.variation?.add_ons != null) {
    const v = item.variation.add_ons;
    addonsList = typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return []; } })() : v;
  }
  if (addonsList == null) return raw;
  if (typeof addonsList === 'string') {
    try { addonsList = JSON.parse(addonsList); } catch { return raw; }
  }
  const allowed = new Set(
    (Array.isArray(addonsList) ? addonsList : [])
      .map((a) => String(a?.addons_name ?? a?.name ?? '').trim().toLowerCase())
      .filter(Boolean)
  );
  if (!allowed.size) return raw;
  return raw.filter((a) => allowed.has(String(a?.name ?? '').trim().toLowerCase()));
};

/**
 * Get effective unit price for a cart item (variation price + add-ons + desserts).
 * Uses only add-ons that belong to this item (getValidAddonsForCartItem).
 */
export const getCartItemUnitPrice = (item) => {
  let basePrice = 0;
  const variation = item.variation || {};

  if (variation && (variation.variation_item_discount != null || variation.variation_item_discount_type)) {
    basePrice = getVariationEffectivePrice(variation);
  } else if (variation && Object.keys(variation).length > 0) {
    basePrice = parseFloat(variation.discount_price != null ? variation.discount_price : variation.price || 0);
  } else {
    basePrice = parseFloat(item.item_price || item.base_price || item.discount_price || item.price || 0);
  }

  const validAddons = getValidAddonsForCartItem(item);
  const addOnsPrice = validAddons.reduce((sum, a) => sum + (parseFloat(a.addons_price ?? a.price) || 0), 0);
  const dessertsPrice = (item.selectedDesserts || []).reduce((sum, d) => sum + (parseFloat(d.price) || 0), 0);
  return basePrice + addOnsPrice + dessertsPrice;
}
